import type {KeyboardEvent, ReactElement} from 'react';
import {useRef} from 'react';
import {Text, TextSize} from '@Components/text';
import {ColorFillPropertyType} from '@Components/color-fill';
import {COLOR_TYPE_TAB_STEP_KEYS, ColorTypeTab, DEFAULT_GRADIENT_TYPE} from './color-type-tabs.types';
import type {ColorTypeTabData, ColorTypeTabsProps} from './color-type-tabs.types';
import styles from './color-type-tabs.module.scss';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';

/** Solid / Gradient tab strip pinned to the top of the popup layout. */
export function ColorTypeTabs({isTypeMixed, ...props}: ColorTypeTabsProps): ReactElement {
  const isGradient = props.type !== ColorFillPropertyType.SOLID;
  const tabListRef = useRef<HTMLDivElement>(null);

  const getTabsData = (): ColorTypeTabData[] => {
    return [
      {id: ColorTypeTab.SOLID, text: window.i18next.t('pmwjs_solid_color'), testId: PosterEditorTestId.COLOR_TYPE_TAB_SOLID},
      {id: ColorTypeTab.GRADIENT, text: window.i18next.t('pmwjs_background_gradient'), testId: PosterEditorTestId.COLOR_TYPE_TAB_GRADIENT},
    ];
  };

  const getSelectedTab = (): ColorTypeTab | null => {
    if (isTypeMixed) {
      return null;
    }
    return isGradient ? ColorTypeTab.GRADIENT : ColorTypeTab.SOLID;
  };

  const selectTab = (tabId: ColorTypeTab): void => {
    if (tabId === getSelectedTab()) {
      return;
    }
    props.onChangeType(tabId === ColorTypeTab.GRADIENT ? DEFAULT_GRADIENT_TYPE : ColorFillPropertyType.SOLID);
  };

  /** A tablist owes arrow-key navigation: without it the roles announce a pattern the keyboard does not honour. */
  const onTabListKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = COLOR_TYPE_TAB_STEP_KEYS[event.key];
    if (step === undefined) {
      return;
    }
    event.preventDefault();
    const tabs = getTabsData();
    const currentIndex = Math.max(
      tabs.findIndex((tab) => {
        return tab.id === getSelectedTab();
      }),
      0
    );
    const nextIndex = (currentIndex + step + tabs.length) % tabs.length;
    selectTab(tabs[nextIndex].id);
    tabListRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[nextIndex]?.focus();
  };

  /** The tab stop sits on the active tab, or on the first one when the selection mixes solid and gradient fills. */
  const isTabFocusable = (tabId: ColorTypeTab, index: number): boolean => {
    const selectedTab = getSelectedTab();
    return selectedTab === null ? index === 0 : tabId === selectedTab;
  };

  const getTab = (tab: ColorTypeTabData, index: number): ReactElement => {
    const isActive = tab.id === getSelectedTab();
    return (
      <button
        key={tab.id}
        type="button"
        role="tab"
        data-testid={tab.testId}
        aria-selected={isActive}
        tabIndex={isTabFocusable(tab.id, index) ? 0 : -1}
        className={`${styles.colorTypeTab} ${isActive ? styles.activeColorTypeTab : ''}`}
        onClick={(): void => {
          selectTab(tab.id);
        }}
      >
        <Text val={tab.text} size={TextSize.MD} bold={isActive} className={`capitalize-sentence ${styles.colorTypeTabLabel}`} />
      </button>
    );
  };

  return (
    <div ref={tabListRef} role="tablist" aria-label={window.i18next.t('pmwjs_color_type')} className={`flex-h-row ${styles.colorTypeTabStrip}`} onKeyDown={onTabListKeyDown}>
      {getTabsData().map(getTab)}
    </div>
  );
}
